import { Box, Button, useToast } from "@chakra-ui/react";
import { observer } from "mobx-react";
import { ApiMessageStack, Message, TableV2 } from "components";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { IntegrationEntitySyncStatusEnum } from "enums/integration-entity-sync-status.enum";
import { AlertStatusEnum } from "enums/alert-status.enum";
import {
  DEFAULT_ERROR_TOAST_OPTIONS,
  DEFAULT_SUCCESS_TOAST_OPTIONS,
} from "constants/default-toast-options";
import { keyBy } from "lodash";
import { UserAccountStore } from "store/UserAccounts/UserAccount/UserAccount.store";
import { syncAltoNegotiatorTableColumnDef } from "./syncAltoNegotiatorTableColumnDef";
import { TAltoNegotiatorRelationExtendedData } from "types/alto.type";
import { IntegrationConfiguratorLayout } from "../../IntegrationConfiguratorLayout";
import { SyncStatusFilter } from "../../SyncStatusFilter";

const DEFAULT_PAGE_SIZE = 10;

interface IProps {
  accountStore: UserAccountStore;
  isManuallyTriggered: boolean;
}

export const AltoIntegrationsNegotiatorsConfigurator: FC<IProps> = observer(
  ({ accountStore, isManuallyTriggered }) => {
    const toast = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [newRelations, setNewRelations] = useState<
      Record<string, TAltoNegotiatorRelationExtendedData>
    >({});

    const accountIntegrationsStore = accountStore.accountIntegrationsStore;
    const accountIntegrationsAltoStore =
      accountIntegrationsStore.accountIntegrationAltoStore;
    const accountIntegrationAltoNegotiatorsStore =
      accountIntegrationsAltoStore.accountIntegrationAltoNegotiatorsStore;
    const {
      altoSyncNegotiatorsData,
      filteredAltoSyncNegotiatorData,
      getSyncStatusFilter: syncStatusFilter,
      setSyncStatusFilter,
    } = accountIntegrationAltoNegotiatorsStore;

    const updateSyncPair = useCallback(
      (syncPair: TAltoNegotiatorRelationExtendedData) => {
        setNewRelations({
          ...newRelations,
          [syncPair.userAccountId]: syncPair,
        });
      },
      [setNewRelations, newRelations]
    );

    useEffect(() => {
      const suggestedArray = altoSyncNegotiatorsData.filter(
        syncData =>
          syncData.syncStatus === IntegrationEntitySyncStatusEnum.SUGGESTED_SYNC
      );
      const suggestedMap = keyBy<TAltoNegotiatorRelationExtendedData>(
        suggestedArray,
        syncData => syncData.userAccountId
      );
      setNewRelations({
        ...suggestedMap,
      });
    }, [altoSyncNegotiatorsData]);

    const columnsDef = useMemo(() => {
      return syncAltoNegotiatorTableColumnDef(accountStore, updateSyncPair);
    }, [accountStore, updateSyncPair]);

    const dataSource = useMemo(() => {
      return filteredAltoSyncNegotiatorData.map(syncData => {
        if (!!newRelations[syncData.userAccountId])
          return newRelations[syncData.userAccountId];

        return syncData;
      });
    }, [filteredAltoSyncNegotiatorData, newRelations]);

    const submitSyncPairs = useCallback(async () => {
      try {
        setIsSubmitting(true);
        await accountIntegrationAltoNegotiatorsStore.updateAccountAltoSyncedNegotiators(
          Object.values(newRelations)
        );
        toast({
          ...DEFAULT_SUCCESS_TOAST_OPTIONS,
          description: (
            <ApiMessageStack
              messageStack={"Alto integration user configuration updated."}
            />
          ),
        });
      } catch (error) {
        toast({
          ...DEFAULT_ERROR_TOAST_OPTIONS,
          description: <ApiMessageStack messageStack={error.message} />,
        });
      } finally {
        setIsSubmitting(false);
      }
    }, [newRelations, accountIntegrationAltoNegotiatorsStore, toast]);

    const banner = useMemo(() => {
      const notSyncedArray = altoSyncNegotiatorsData.filter(
        syncData =>
          syncData.syncStatus !== IntegrationEntitySyncStatusEnum.SYNCED
      );

      if (notSyncedArray.length > 0) {
        return (
          <Message status={AlertStatusEnum.WARNING}>
            <Box>
              {`${notSyncedArray.length} of your negotiators are not syncing to LeadPro`}
            </Box>
          </Message>
        );
      }

      return null;
    }, [altoSyncNegotiatorsData]);

    const handleSyncFilterChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setSyncStatusFilter(e.target.checked);
      },
      [setSyncStatusFilter]
    );

    return (
      <IntegrationConfiguratorLayout disabled={!isManuallyTriggered}>
        <Box fontSize={"xl"}>Negotiator mappings</Box>
        <Box fontSize={"md"} color={"leadpro.400"}>
          Using the manual Alto integration allows you to map your leadpro users
          to your Alto negotiators.
        </Box>
        {banner}
        <TableV2<TAltoNegotiatorRelationExtendedData>
          globalFilterInputPlaceholder={"Search users by name"}
          dataSource={dataSource}
          additionalActions={
            <SyncStatusFilter
              isChecked={syncStatusFilter}
              onChange={handleSyncFilterChange}
            />
          }
          columns={columnsDef}
          pageSize={DEFAULT_PAGE_SIZE}
        />
        <Box width={"100%"} textAlign={"right"}>
          <Button
            colorScheme={"blue"}
            onClick={submitSyncPairs}
            isLoading={isSubmitting}
            disabled={!isManuallyTriggered}
          >
            Update
          </Button>
        </Box>
      </IntegrationConfiguratorLayout>
    );
  }
);
